import type { BuilderBlockMetadataResponse } from 'types/builderBlockMetadata';

/** Resolve parent indicator id from a grid row id (parent or chevron child). */
export function resolveIndicatorIdFromRowSelection(
  rowId: string | number | undefined,
  dimensionalIndicatorsV2: any[] | null | undefined,
  indicatorsV2: any[] | null | undefined,
  indicators: any[] | null | undefined,
): number | null {
  if (rowId == null) return null;

  if (typeof rowId === 'number' && !Number.isNaN(rowId)) return rowId;

  const numericRowId = Number(rowId);
  if (!Number.isNaN(numericRowId) && numericRowId > 0) return numericRowId;

  const flatRows = dimensionalIndicatorsV2 ?? [];
  const flatMatch = flatRows.find((row) => {
    const gridRowId = row?.id ?? `${row?.name}_${row?.id ?? row?.parentId}`;
    return String(gridRowId) === String(rowId) || String(row?.id) === String(rowId);
  });
  if (flatMatch) {
    const parentId = flatMatch.parentId ?? flatMatch.indicatorId ?? flatMatch.id;
    const parsed = Number(parentId);
    if (!Number.isNaN(parsed) && parsed > 0) return parsed;
  }

  const source = (indicatorsV2?.length ? indicatorsV2 : indicators) ?? [];
  const outputMatch = source.find((row) => {
    const gridRowId = row?.id ?? `${row?.name}_${row?.id ?? row?.parentId}`;
    return String(gridRowId) === String(rowId) || String(row?.id) === String(rowId);
  });
  if (outputMatch?.id != null) {
    const parsed = Number(outputMatch.id);
    if (!Number.isNaN(parsed) && parsed > 0) return parsed;
  }

  if (typeof rowId === 'string') {
    const trailingId = Number(rowId.split('_').at(-1));
    if (!Number.isNaN(trailingId) && trailingId > 0) return trailingId;
  }

  return null;
}

export function findOutputRowForIndicatorId(
  indicatorId: number,
  indicatorsV2: any[] | null | undefined,
  indicators: any[] | null | undefined,
): Record<string, any> | null {
  const source = (indicatorsV2?.length ? indicatorsV2 : indicators) ?? [];
  return source.find((row) => Number(row?.id) === indicatorId) ?? null;
}

/**
 * Toolbar / formula bar source of truth: `get_block_metadata` indicator row by id.
 * Output row fields (e.g. `data_input`) are merged underneath for the input panel.
 */
export function resolveSelectedIndicatorFromMetadata(
  blockMetadata: BuilderBlockMetadataResponse | null | undefined,
  indicatorId: number | null,
  outputRow?: Record<string, any> | null,
): Record<string, any> | null {
  if (indicatorId == null) return null;

  const metadataRow = blockMetadata?.indicators?.find(
    (indicator) => Number(indicator.id) === indicatorId,
  );

  if (!metadataRow && !outputRow) return null;

  const indicatorType = (metadataRow?.indicator_type ?? outputRow?.indicator_type ?? 'input')
    .toString()
    .toLowerCase();

  return {
    ...outputRow,
    ...metadataRow,
    id: indicatorId,
    name: metadataRow?.name ?? outputRow?.name ?? '',
    indicator_type: indicatorType,
  };
}

export type ToolbarIndicatorType = 'calculation' | 'input';

export function getToolbarIndicatorType(
  indicator: { indicator_type?: string } | null | undefined,
): ToolbarIndicatorType {
  return indicator?.indicator_type?.toLowerCase() === 'calculation' ? 'calculation' : 'input';
}
